import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * Journey QC — Audit view (/admin/journey/audit), Zach 10/23/26.
 *
 * The transparency layer without a canary: pick real prospects at random
 * from the active journey population, read what the sequence was supposed
 * to do against what it did (every send, deferral and skip with its stamped
 * reason), see delivery events, the message thread with time-to-reply, and
 * the next 48 hours — then stamp the prospect as verified. The stamp is a
 * row in journey_qc_audits (migration 0097), so the weekly sample is a record.
 *
 * Read-only except the stamp. Backed by /api/admin/journey-audit.
 */

type SampleRow = {
  profileId: string;
  name: string | null;
  email: string;
  advisor: string | null;
  enrollments: Array<{ enrollmentId: string; slug: string; status: string; enrolledAt: string }>;
  isTest: boolean;
  paused: boolean;
  meetingSoon: boolean;
  lastAuditedAt: string | null;
};

type SamplePayload = {
  ok: boolean;
  error?: string;
  message?: string;
  population: number;
  includeTest: boolean;
  advisors: string[];
  sequences: string[];
  auditTableMissing: boolean;
  sample: SampleRow[];
};

type StepView = {
  stepOrder: number | null;
  templateKey: string;
  channel: string;
  schedule: string;
  state: string;
  flag: string | null;
  note: string | null;
  send: {
    sendId: string;
    subject: string | null;
    scheduledFor: string | null;
    sentAt: string | null;
    skipReason: string | null;
    providerMessageId: string | null;
    hasBody: boolean;
  } | null;
};

type ProspectPayload = {
  ok: boolean;
  error?: string;
  message?: string;
  profile: { profileId: string; name: string | null; email: string; advisor: string | null };
  nextMeeting: { start: string; name: string } | null;
  enrollments: Array<{
    enrollmentId: string;
    sequence: string | null;
    sequenceStatus: string | null;
    status: string;
    isTest: boolean;
    compression: number;
    enrolledAt: string;
    steps: StepView[];
  }>;
  deliveryBySend: Record<string, Array<{ type: string; bounceType: string | null; at: string }>>;
  eventsNote: string | null;
  messages: Array<{
    messageId: string;
    direction: string;
    author: string | null;
    body: string;
    at: string;
    repliedAt?: string;
  }>;
  unanswered: number;
  next48: Array<{ at: string; kind: string; title: string; detail: string | null }>;
  anomalies: string[];
  audits: AuditRow[];
  auditTableMissing: boolean;
};

type LaunchSeq = {
  slug: string;
  status: string;
  triggerEvent: string;
  launch: { since?: string; min?: number; max?: number; per_day?: number; open?: boolean; changed_by?: string; changed_at?: string } | null;
};

type LaunchPayload = {
  ok: boolean;
  error?: string;
  message?: string;
  sequences: LaunchSeq[];
  today: { journey: number; holdout: number; excluded: number };
  recent: Array<{ assignment_id: string; email_normalized: string; investment_amount: number | null; arm: string; reason: string; assigned_at: string }>;
  ledgerMissing: boolean;
};

type AuditRow = {
  audit_id: string;
  profile_id: string;
  enrollment_id: string | null;
  audited_by: string;
  audited_at: string;
  verdict: 'ok' | 'issue';
  note: string | null;
  profile?: { name: string | null; email: string } | null;
};

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d >= 1) return `${d}d ago`;
  const h = Math.floor(ms / 3_600_000);
  if (h >= 1) return `${h}h ago`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
}

function stateClass(state: string): string {
  if (state === 'sent') return 'ok';
  if (state === 'failed' || state === 'missing') return 'bad';
  if (state === 'skipped' || state === 'cancelled') return 'muted';
  return 'wait';
}

const CSS = [
  '.aja-wrap { display: grid; grid-template-columns: minmax(280px, 360px) minmax(0, 1fr); gap: 20px; align-items: start; }',
  '.aja-wrap > div { min-width: 0; }',
  '.aja-steps-wrap { overflow-x: auto; }',
  '.aja-controls { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 10px; }',
  '.aja-controls select, .aja-controls input[type=text] { padding: 7px 9px; border: 1px solid #c4cbd4; border-radius: 6px; font: inherit; font-size: 13px; background: #fff; }',
  '.aja-controls label { font-size: 13px; color: #444; display: inline-flex; gap: 6px; align-items: center; }',
  '.aja-pop { font-size: 12px; color: #6a6f6a; margin-left: 4px; }',
  '.aja-card { background: #fff; border: 1px solid #dfe3e8; border-radius: 8px; padding: 12px 14px; margin-bottom: 10px; cursor: pointer; }',
  '.aja-card:hover { border-color: #0B1F3A; }',
  '.aja-card.on { border-color: #C8102E; box-shadow: 0 0 0 1px #C8102E inset; }',
  '.aja-card .nm { font-weight: 600; font-size: 14px; }',
  '.aja-card .em { font-size: 12px; color: #6a6f6a; word-break: break-all; }',
  '.aja-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }',
  '.aja-chip { font-size: 11px; border: 1px solid #d6dbe2; border-radius: 10px; padding: 1px 8px; color: #444; background: #f6f8fa; }',
  '.aja-chip.test { border-color: #C8102E; color: #C8102E; background: #fff; }',
  '.aja-chip.soon { border-color: #0B1F3A; color: #0B1F3A; }',
  '.aja-chip.paused { border-color: #b4760a; color: #b4760a; background: #fff8e6; }',
  '.aja-chip.ok { border-color: #1f7a3a; color: #1f7a3a; background: #eef8f0; }',
  '.aja-chip.bad { border-color: #C8102E; color: #C8102E; background: #fff1f1; }',
  '.aja-detail { background: #fff; border: 1px solid #dfe3e8; border-radius: 8px; padding: 18px 20px; }',
  '.aja-detail h2 { font-family: Georgia, serif; font-weight: normal; font-size: 22px; color: #0B1F3A; margin: 0 0 4px; }',
  '.aja-detail h3 { font-size: 13px; letter-spacing: 0.6px; text-transform: uppercase; color: #6a6f6a; margin: 22px 0 8px; }',
  '.aja-meta { font-size: 13px; color: #6a6f6a; }',
  '.aja-banner { border-radius: 8px; padding: 10px 14px; margin: 14px 0; font-size: 13.5px; }',
  '.aja-banner.ok { background: #eef8f0; border: 1px solid #bfe3c8; color: #1f5a2d; }',
  '.aja-banner.bad { background: #fff1f1; border: 1px solid #f0b4b4; color: #8a1120; }',
  '.aja-banner ul { margin: 6px 0 0; padding-left: 18px; }',
  '.aja-enr { border: 1px solid #eef1f4; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; }',
  '.aja-enr-head { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; font-size: 13.5px; margin-bottom: 6px; }',
  '.aja-enr-head strong { font-size: 14.5px; }',
  '.aja-steps { width: 100%; min-width: 560px; border-collapse: collapse; font-size: 13px; }',
  '.aja-steps th { text-align: left; font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; color: #6a6f6a; padding: 6px 8px; border-bottom: 1px solid #dfe3e8; }',
  '.aja-steps td { padding: 8px; border-bottom: 1px solid #eef1f4; vertical-align: top; }',
  '.aja-state { display: inline-block; font-size: 11px; border-radius: 10px; padding: 1px 8px; border: 1px solid #d6dbe2; white-space: nowrap; }',
  '.aja-state.ok { background: #eef8f0; border-color: #bfe3c8; color: #1f5a2d; }',
  '.aja-state.bad { background: #fff1f1; border-color: #f0b4b4; color: #8a1120; }',
  '.aja-state.wait { background: #f6f8fa; color: #444; }',
  '.aja-state.muted { background: #f3f3f3; color: #777; }',
  '.aja-flag { color: #8a1120; font-size: 12.5px; margin-top: 3px; }',
  '.aja-note { color: #6a6f6a; font-size: 12px; margin-top: 3px; }',
  '.aja-deliv { font-size: 11.5px; color: #444; margin-top: 3px; }',
  '.aja-link { background: none; border: none; padding: 0; color: #0B1F3A; text-decoration: underline; cursor: pointer; font: inherit; font-size: 12.5px; }',
  '.aja-mail { border: 1px solid #dfe3e8; border-radius: 6px; margin-top: 8px; background: #fff; }',
  '.aja-mail iframe { width: 100%; height: 520px; border: 0; }',
  '.aja-msg { border-left: 3px solid #c4cbd4; padding: 6px 10px; margin-bottom: 8px; font-size: 13px; background: #fafbfc; }',
  '.aja-msg.team { border-left-color: #C8102E; }',
  '.aja-msg .who { font-size: 11.5px; color: #6a6f6a; margin-bottom: 2px; }',
  '.aja-msg .late { color: #8a1120; }',
  '.aja-next { font-size: 13px; }',
  '.aja-next li { margin-bottom: 4px; }',
  '.aja-stamp { border-top: 1px solid #dfe3e8; margin-top: 18px; padding-top: 14px; }',
  '.aja-stamp textarea { width: 100%; box-sizing: border-box; min-height: 70px; border: 1px solid #c4cbd4; border-radius: 6px; padding: 8px 10px; font: inherit; font-size: 13px; margin: 8px 0; }',
  '.aja-stamp .row { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; font-size: 13.5px; }',
  '.aja-btn { background: #0B1F3A; color: #fff; border: none; border-radius: 4px; padding: 9px 16px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }',
  '.aja-btn.ghost { background: #fff; color: #0B1F3A; border: 1px solid #0B1F3A; }',
  '.aja-btn:disabled { opacity: 0.5; cursor: default; }',
  '.aja-log td { font-size: 13px; }',
  '.aja-gate { background: #fff; border: 1px solid #dfe3e8; border-left: 5px solid #1f7a3a; border-radius: 8px; padding: 12px 16px; margin-bottom: 18px; display: flex; flex-wrap: wrap; gap: 10px 22px; align-items: center; font-size: 13.5px; }',
  '.aja-gate.closed { border-left-color: #C8102E; }',
  '.aja-gate.dark { border-left-color: #8C8C8C; }',
  '.aja-gate .ttl { font-weight: 700; font-size: 14px; }',
  '.aja-gate .st { display: inline-block; font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; border-radius: 10px; padding: 2px 9px; border: 1px solid #d6dbe2; background: #f6f8fa; margin-left: 6px; }',
  '.aja-gate .st.live { background: #eef8f0; border-color: #bfe3c8; color: #1f5a2d; }',
  '.aja-gate .st.open { background: #eef8f0; border-color: #bfe3c8; color: #1f5a2d; }',
  '.aja-gate .st.paused { background: #fff1f1; border-color: #f0b4b4; color: #8a1120; }',
  '.aja-gate .meta { color: #6a6f6a; }',
  '.aja-gate .spacer { flex: 1 1 auto; }',
  '.aja-gate .aja-btn.stop { background: #C8102E; }',
  '@media (max-width: 900px) { .aja-wrap { grid-template-columns: 1fr; } }',
].join('\n');

export function AdminJourneyAudit() {
  const auth = useCurrentUser();

  const [n, setN] = useState(5);
  const [advisor, setAdvisor] = useState('');
  const [sequence, setSequence] = useState('');
  const [includeTest, setIncludeTest] = useState(true);
  const [sample, setSample] = useState<SamplePayload | null>(null);
  const [sampling, setSampling] = useState(false);
  const [sampleErr, setSampleErr] = useState<string | null>(null);

  const [selected, setSelected] = useState<string | null>(null);
  const [prospect, setProspect] = useState<ProspectPayload | null>(null);
  const [prospectErr, setProspectErr] = useState<string | null>(null);
  const [openSend, setOpenSend] = useState<string | null>(null);
  const [sendCache, setSendCache] = useState<Record<string, any>>({});

  const [verdict, setVerdict] = useState<'ok' | 'issue'>('ok');
  const [note, setNote] = useState('');
  const [stamping, setStamping] = useState(false);
  const [stampMsg, setStampMsg] = useState<string | null>(null);

  const [log, setLog] = useState<AuditRow[] | null>(null);
  const [logMissing, setLogMissing] = useState(false);

  const [gate, setGate] = useState<LaunchPayload | null>(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [gateMsg, setGateMsg] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status === 'unauthenticated') window.location.href = redirectToPortalLogin();
  }, [auth.status]);

  const isAdmin = auth.status === 'authenticated' && auth.user.isAdmin;

  async function loadGate() {
    try {
      const res = await authedFetch('/api/admin/journey-launch', { cache: 'no-store' });
      const json = (await res.json()) as LaunchPayload;
      if (json.ok) setGate(json);
    } catch {
      /* fail-soft: the panel just doesn't render */
    }
  }

  async function setEnrollment(slug: string, open: boolean) {
    const verb = open ? 'Resume' : 'Pause';
    if (!window.confirm(`${verb} new enrollments on "${slug}"? ${open ? 'The gate will enroll new band bookings again.' : 'Prospects already enrolled keep going; new bookings become holdouts until you resume.'}`)) return;
    setGateBusy(true);
    setGateMsg(null);
    try {
      const res = await authedFetch('/api/admin/journey-launch', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ slug, open }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.message ?? json.error ?? 'Request failed');
      setGateMsg(`${open ? 'Enrollment resumed' : 'Enrollment paused'} on ${slug} at ${fmtTs(json.launch?.changed_at)}. The runner picks this up on its next tick (≤5 min).`);
      await loadGate();
    } catch (e) {
      setGateMsg(e instanceof Error ? e.message : 'Network error');
    } finally {
      setGateBusy(false);
    }
  }

  function renderGate() {
    if (!gate) return null;
    const gated = gate.sequences.filter((s) => s.launch);
    if (!gated.length) {
      return (
        <div className="aja-gate dark">
          <span className="ttl">Launch gate</span>
          <span className="meta">
            No sequence carries a launch block yet. Sequences: {gate.sequences.map((s) => `${s.slug} (${s.status})`).join(', ') || 'none'}.
          </span>
        </div>
      );
    }
    return (
      <>
        {gated.map((s) => {
          const l = s.launch!;
          const open = l.open !== false;
          const live = s.status === 'live';
          const band = l.min != null || l.max != null ? `$${((l.min ?? 0) / 1000).toFixed(0)}k–${l.max != null ? `$${(l.max / 1000).toFixed(0)}k` : 'any'}` : 'any amount';
          return (
            <div className={'aja-gate ' + (!live ? 'dark' : open ? '' : 'closed')} key={s.slug}>
              <span className="ttl">
                Launch gate · {s.slug}
                <span className={'st ' + (live ? 'live' : '')}>{s.status}</span>
                <span className={'st ' + (open ? 'open' : 'paused')}>{open ? 'enrolling' : 'paused'}</span>
              </span>
              <span className="meta">
                {band} · {l.per_day != null ? `${l.per_day}/day` : 'no daily cap'}
                {l.since ? ` · bookings after ${fmtTs(l.since)}` : ''}
              </span>
              {!gate.ledgerMissing && (
                <span className="meta">
                  today: <strong>{gate.today.journey}</strong> enrolled · {gate.today.holdout} holdout · {gate.today.excluded} outside band
                </span>
              )}
              {gate.ledgerMissing && <span className="aja-flag">cohort ledger missing (run 0098) — the gate fails closed</span>}
              {l.changed_by && <span className="meta">last change {l.changed_by} {fmtTs(l.changed_at)}</span>}
              <span className="spacer" />
              {open ? (
                <button type="button" className="aja-btn stop" disabled={gateBusy} onClick={() => void setEnrollment(s.slug, false)}>
                  {gateBusy ? 'Saving…' : 'Pause enrollment'}
                </button>
              ) : (
                <button type="button" className="aja-btn" disabled={gateBusy} onClick={() => void setEnrollment(s.slug, true)}>
                  {gateBusy ? 'Saving…' : 'Resume enrollment'}
                </button>
              )}
            </div>
          );
        })}
        {gateMsg && <p className="aja-meta" style={{ marginTop: -8, marginBottom: 14 }}>{gateMsg}</p>}
      </>
    );
  }

  async function loadLog() {
    try {
      const res = await authedFetch('/api/admin/journey-audit?view=log', { cache: 'no-store' });
      const json = await res.json();
      if (json.ok) {
        setLog(json.audits ?? []);
        setLogMissing(!!json.auditTableMissing);
      }
    } catch {
      /* fail-soft: the log is a convenience */
    }
  }

  async function pick() {
    setSampling(true);
    setSampleErr(null);
    setSelected(null);
    setProspect(null);
    try {
      const url = new URL('/api/admin/journey-audit', window.location.origin);
      url.searchParams.set('view', 'sample');
      url.searchParams.set('n', String(n));
      if (advisor) url.searchParams.set('advisor', advisor);
      if (sequence) url.searchParams.set('sequence', sequence);
      if (includeTest) url.searchParams.set('test', '1');
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = (await res.json()) as SamplePayload;
      if (!json.ok) throw new Error(json.message ?? json.error ?? 'Request failed');
      setSample(json);
      if (json.sample.length) openProspect(json.sample[0].profileId);
    } catch (e) {
      setSampleErr(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSampling(false);
    }
  }

  async function openProspect(profileId: string) {
    setSelected(profileId);
    setProspect(null);
    setProspectErr(null);
    setOpenSend(null);
    setStampMsg(null);
    setNote('');
    setVerdict('ok');
    try {
      const url = new URL('/api/admin/journey-audit', window.location.origin);
      url.searchParams.set('view', 'prospect');
      url.searchParams.set('profile', profileId);
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = (await res.json()) as ProspectPayload;
      if (!json.ok) throw new Error(json.message ?? json.error ?? 'Request failed');
      setProspect(json);
      if (json.anomalies.length) setVerdict('issue');
    } catch (e) {
      setProspectErr(e instanceof Error ? e.message : 'Network error');
    }
  }

  async function toggleSend(sendId: string) {
    if (openSend === sendId) {
      setOpenSend(null);
      return;
    }
    setOpenSend(sendId);
    if (sendCache[sendId]) return;
    try {
      const url = new URL('/api/admin/journey-qc', window.location.origin);
      url.searchParams.set('send', sendId);
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = await res.json();
      setSendCache((c) => ({ ...c, [sendId]: json.ok ? json.send : { error: json.error ?? 'not found' } }));
    } catch {
      setSendCache((c) => ({ ...c, [sendId]: { error: 'Network error' } }));
    }
  }

  async function stamp() {
    if (!prospect) return;
    setStamping(true);
    setStampMsg(null);
    try {
      const snapshot = {
        anomalies: prospect.anomalies,
        enrollments: prospect.enrollments.map((e) => ({
          sequence: e.sequence,
          status: e.status,
          steps: e.steps.map((s) => ({ templateKey: s.templateKey, state: s.state, flag: s.flag })),
        })),
        unanswered: prospect.unanswered,
      };
      const res = await authedFetch('/api/admin/journey-audit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          profileId: prospect.profile.profileId,
          enrollmentId: prospect.enrollments[0]?.enrollmentId ?? null,
          verdict,
          note: note.trim() || null,
          snapshot,
        }),
      });
      const json = await res.json();
      if (!json.ok) {
        if (json.error === 'audit_table_missing') throw new Error('The audit table is not there yet (migration 0097). The verification was not saved.');
        throw new Error(json.message ?? json.error ?? 'Request failed');
      }
      setProspect({ ...prospect, audits: [json.audit, ...prospect.audits] });
      setStampMsg(`Recorded as ${verdict === 'ok' ? 'verified' : 'issue'} at ${fmtTs(json.audit.audited_at)}.`);
      setNote('');
      void loadLog();
      setSample((s) =>
        s
          ? {
              ...s,
              sample: s.sample.map((r) => (r.profileId === prospect.profile.profileId ? { ...r, lastAuditedAt: json.audit.audited_at } : r)),
            }
          : s
      );
    } catch (e) {
      setStampMsg(e instanceof Error ? e.message : 'Network error');
    } finally {
      setStamping(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    void loadLog();
    void loadGate();
    const t = window.setInterval(() => void loadGate(), 60_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  function renderProspect() {
    if (prospectErr)
      return (
        <div className="admin-err" role="alert">
          <strong>Error:</strong> {prospectErr}
        </div>
      );
    if (!selected) return <p className="admin-empty">Pick a sample, then click a prospect to audit them.</p>;
    if (!prospect) return <p className="admin-empty">Loading…</p>;
    const p = prospect;
    const qcHref = '/admin/journey?email=' + encodeURIComponent(p.profile.email);
    return (
      <div className="aja-detail">
        <h2>{p.profile.name ?? p.profile.email}</h2>
        <div className="aja-meta">
          {p.profile.email}
          {p.profile.advisor ? ` · advisor: ${p.profile.advisor}` : ' · no owner advisor'}
          {p.nextMeeting ? ` · next meeting: ${p.nextMeeting.name} ${fmtTs(p.nextMeeting.start)}` : ' · no next meeting booked'}
          {' · '}
          <a href={qcHref} target="_blank" rel="noopener noreferrer">
            full timeline ↗
          </a>
        </div>

        {p.anomalies.length ? (
          <div className="aja-banner bad">
            <strong>{p.anomalies.length} thing{p.anomalies.length === 1 ? '' : 's'} to look at</strong>
            <ul>
              {p.anomalies.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="aja-banner ok">
            <strong>No anomalies.</strong> Every materialized send fired on time or carries a stamped deferral, nothing failed, and no message is waiting on a reply.
          </div>
        )}

        <h3>Expected vs actual</h3>
        {p.enrollments.length === 0 && <p className="admin-empty">No journey enrollments for this prospect.</p>}
        {p.enrollments.map((e) => (
          <div className="aja-enr" key={e.enrollmentId}>
            <div className="aja-enr-head">
              <strong>{e.sequence ?? 'sequence'}</strong>
              <span className={'aja-chip ' + (e.status === 'active' ? 'ok' : e.status === 'paused_reply' ? 'paused' : '')}>{e.status}</span>
              {e.isTest && <span className="aja-chip test">test ×{e.compression}</span>}
              <span className="aja-meta">enrolled {fmtTs(e.enrolledAt)}</span>
              {e.sequenceStatus && e.sequenceStatus !== 'live' && <span className="aja-chip">sequence {e.sequenceStatus}</span>}
            </div>
            <div className="aja-steps-wrap">
            <table className="aja-steps">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Planned</th>
                  <th>Actual</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {e.steps.map((s, i) => {
                  const deliv = s.send ? p.deliveryBySend[s.send.sendId] ?? p.deliveryBySend[s.send.providerMessageId ?? ''] ?? [] : [];
                  const cached = s.send ? sendCache[s.send.sendId] : null;
                  return (
                    <tr key={s.templateKey + i}>
                      <td>
                        <div>{s.templateKey}</div>
                        {s.send?.subject && <div className="aja-note">{s.send.subject}</div>}
                      </td>
                      <td>{s.schedule}</td>
                      <td>
                        <span className={'aja-state ' + stateClass(s.state)}>{s.state}</span>
                        {s.send?.skipReason && <div className="aja-note">{s.send.skipReason}</div>}
                        {s.flag && <div className="aja-flag">{s.flag}</div>}
                        {s.note && <div className="aja-note">{s.note}</div>}
                        {deliv.length > 0 && (
                          <div className="aja-deliv">
                            {deliv.map((d, j) => (
                              <span key={j}>
                                {j ? ' · ' : ''}
                                {d.type}
                                {d.bounceType ? ` (${d.bounceType})` : ''} {fmtTs(d.at)}
                              </span>
                            ))}
                          </div>
                        )}
                        {s.send?.hasBody && (
                          <div>
                            <button type="button" className="aja-link" onClick={() => toggleSend(s.send!.sendId)}>
                              {openSend === s.send.sendId ? 'hide email' : 'view email'}
                            </button>
                          </div>
                        )}
                        {s.send && openSend === s.send.sendId && (
                          <div className="aja-mail">
                            {!cached && <p className="admin-empty">Loading…</p>}
                            {cached?.error && <p className="admin-empty">{cached.error}</p>}
                            {cached?.body_html && <iframe title="delivered email" sandbox="" srcDoc={cached.body_html} />}
                            {cached && !cached.body_html && cached.body_text && <pre style={{ whiteSpace: 'pre-wrap', padding: 12, margin: 0, fontSize: 13 }}>{cached.body_text}</pre>}
                          </div>
                        )}
                      </td>
                      <td>
                        {s.send?.sentAt ? (
                          <>
                            sent {fmtTs(s.send.sentAt)}
                            {s.send.scheduledFor && <div className="aja-note">planned {fmtTs(s.send.scheduledFor)}</div>}
                          </>
                        ) : s.send?.scheduledFor ? (
                          <>planned {fmtTs(s.send.scheduledFor)}</>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        ))}
        {p.eventsNote && <p className="aja-note">{p.eventsNote}</p>}

        <h3>Messages {p.unanswered ? `· ${p.unanswered} unanswered` : ''}</h3>
        {p.messages.length === 0 && <p className="admin-empty">No Decision Center messages.</p>}
        {p.messages.map((m) => {
          const waitH = m.direction === 'prospect' ? ((m.repliedAt ? new Date(m.repliedAt).getTime() : Date.now()) - new Date(m.at).getTime()) / 3_600_000 : 0;
          return (
            <div className={'aja-msg ' + (m.direction === 'team' ? 'team' : '')} key={m.messageId}>
              <div className="who">
                {m.direction === 'team' ? `Reply from ${m.author ?? 'the team'}` : 'Prospect'} · {fmtTs(m.at)}
                {m.direction === 'prospect' && (
                  <span className={waitH > 4 ? ' late' : ''}>
                    {' · '}
                    {m.repliedAt ? `answered in ${Math.round(waitH * 10) / 10}h` : `unanswered for ${Math.round(waitH * 10) / 10}h`}
                  </span>
                )}
              </div>
              <div>{m.body}</div>
            </div>
          );
        })}

        <h3>Next 48 hours</h3>
        {p.next48.length === 0 ? (
          <p className="admin-empty">Nothing scheduled in the next 48 hours.</p>
        ) : (
          <ul className="aja-next">
            {p.next48.map((x, i) => (
              <li key={i}>
                <strong>{fmtTs(x.at)}</strong> · {x.kind === 'send' ? 'send' : 'meeting'}: {x.title}
                {x.detail ? <span className="aja-note"> · {x.detail}</span> : null}
              </li>
            ))}
          </ul>
        )}

        <div className="aja-stamp">
          <h3 style={{ marginTop: 0 }}>Audit</h3>
          {p.audits.length > 0 && (
            <p className="aja-meta">
              Previously: {p.audits.map((a) => `${a.verdict} by ${a.audited_by} ${fmtTs(a.audited_at)}`).join(' · ')}
            </p>
          )}
          {p.auditTableMissing && <p className="aja-flag">Audit table not found (migration 0097 not run yet). Verifications will not save until it is.</p>}
          <div className="row">
            <label>
              <input type="radio" name="verdict" checked={verdict === 'ok'} onChange={() => setVerdict('ok')} /> Verified, working as intended
            </label>
            <label>
              <input type="radio" name="verdict" checked={verdict === 'issue'} onChange={() => setVerdict('issue')} /> Issue found
            </label>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you checked, what you saw (optional for a clean pass; say what's wrong for an issue)." />
          <div className="row">
            <button type="button" className="aja-btn" disabled={stamping} onClick={() => void stamp()}>
              {stamping ? 'Saving…' : verdict === 'ok' ? 'Mark verified' : 'Record issue'}
            </button>
            {stampMsg && <span className="aja-meta">{stampMsg}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-app">
      <style>{CSS}</style>
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1>Journey QC · Audit</h1>
            <p className="admin-sub">Pick real prospects at random, check what the journey did against what it was supposed to do, and stamp the result.</p>
          </div>
        </div>
        <div className="admin-filters">
          <div className="aja-controls">
            <label>
              Sample
              <select value={n} onChange={(e) => setN(parseInt(e.target.value, 10))}>
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
              </select>
            </label>
            <label>
              Advisor
              <select value={advisor} onChange={(e) => setAdvisor(e.target.value)}>
                <option value="">any</option>
                {(sample?.advisors ?? []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sequence
              <select value={sequence} onChange={(e) => setSequence(e.target.value)}>
                <option value="">any</option>
                {(sample?.sequences ?? []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} /> include test profiles
            </label>
            <button type="button" className="aja-btn" disabled={sampling || !isAdmin} onClick={() => void pick()}>
              {sampling ? 'Picking…' : 'Pick for me'}
            </button>
            {sample && (
              <span className="aja-pop">
                {sample.population} prospect{sample.population === 1 ? '' : 's'} in the population
                {sample.includeTest ? ' (test included)' : ''}
              </span>
            )}
          </div>
        </div>
        <nav className="admin-nav">
          <a href="/admin/journey" className="admin-nav-link">
            Journey QC console
          </a>
          <a href="/admin/routing" className="admin-nav-link">
            Routing Log
          </a>
          <a href="/admin/advisors" className="admin-nav-link">
            Advisors
          </a>
        </nav>
      </header>

      <main className="admin-main">
        {auth.status === 'loading' && <p className="admin-empty">Checking session…</p>}
        {auth.status === 'authenticated' && !auth.user.isAdmin && (
          <div className="admin-err" role="alert">
            Admin access required.
          </div>
        )}
        {isAdmin && (
          <>
            {renderGate()}
            {sampleErr && (
              <div className="admin-err" role="alert">
                <strong>Error:</strong> {sampleErr}
              </div>
            )}
            <div className="aja-wrap">
              <div>
                {!sample && <p className="admin-empty">Choose a sample size and click Pick for me.</p>}
                {sample && sample.sample.length === 0 && <p className="admin-empty">No prospects match. Widen the filters or include test profiles.</p>}
                {sample?.sample.map((r) => (
                  <div
                    key={r.profileId}
                    className={'aja-card ' + (selected === r.profileId ? 'on' : '')}
                    role="button"
                    tabIndex={0}
                    onClick={() => void openProspect(r.profileId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void openProspect(r.profileId);
                      }
                    }}
                  >
                    <div className="nm">{r.name ?? r.email}</div>
                    <div className="em">
                      {r.email}
                      {r.advisor ? ` · ${r.advisor}` : ''}
                    </div>
                    <div className="aja-chips">
                      {r.enrollments.map((e) => (
                        <span key={e.enrollmentId} className={'aja-chip ' + (e.status === 'paused_reply' ? 'paused' : '')}>
                          {e.slug} · {e.status}
                        </span>
                      ))}
                      {r.isTest && <span className="aja-chip test">test</span>}
                      {r.meetingSoon && <span className="aja-chip soon">meeting this week</span>}
                      <span className="aja-chip">audited {ago(r.lastAuditedAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div>{renderProspect()}</div>
            </div>

            <h3 style={{ fontSize: 13, letterSpacing: 0.6, textTransform: 'uppercase', color: '#6a6f6a', margin: '28px 0 8px' }}>Audit log</h3>
            {logMissing && <p className="admin-empty">The audit log appears once migration 0097 (journey_qc_audits) has run.</p>}
            {log && log.length === 0 && !logMissing && <p className="admin-empty">No audits recorded yet.</p>}
            {log && log.length > 0 && (
              <div className="admin-table-wrap">
                <table className="admin-table aja-log">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Prospect</th>
                      <th>Verdict</th>
                      <th>By</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((a) => (
                      <tr key={a.audit_id}>
                        <td>{fmtTs(a.audited_at)}</td>
                        <td>
                          <button type="button" className="aja-link" onClick={() => void openProspect(a.profile_id)}>
                            {a.profile?.name ?? a.profile?.email ?? a.profile_id.slice(0, 8)}
                          </button>
                        </td>
                        <td>
                          <span className={'aja-chip ' + (a.verdict === 'ok' ? 'ok' : 'bad')}>{a.verdict === 'ok' ? 'verified' : 'issue'}</span>
                        </td>
                        <td>{a.audited_by}</td>
                        <td>{a.note ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
